// Voice-bench WebSocket client for /ws/studio/guide-voice-test/{id}.
//
// Rendering mirrors the mobile client (aura-mobile voice_turn_controller.dart)
// rather than grouping by the server's `turnId`: the server stamps partials
// with a stale turn id (assigned late, at eager/final), so turnId-bucketing
// shows one utterance as two user bubbles. Instead: user partials overwrite a
// single live bubble; a user `isFinal` commits the utterance and opens a new
// streaming guide bubble; model deltas append to that bubble. Once the server
// assigns turn ids at StartOfTurn, both clients can group by turnId again.

import { WS_BASE_URL } from "../config/env";
import { getIdToken } from "./auth";
import { startAudioPlayerWorklet } from "../audio/audio-player";
import { startAudioRecorderWorklet, stopMicrophone } from "../audio/audio-recorder";

export interface InterruptionMarker {
  interruptedAtMs: number;
  heardRatio: number;
  intendedText: string;
  spokenText: string;
}

export interface Turn {
  id: string;
  user: string;
  guide: string;
  interruption?: InterruptionMarker;
}

export interface VoiceBenchCallbacks {
  onTurns: (turns: Turn[]) => void;
  onStatus: (text: string, live: boolean) => void;
  onSystem: (text: string) => void;
}

interface WsEnvelope {
  turnId?: string;
  payload?: {
    eventType: string;
    data?: string;
    mimeType?: string;
    role?: string;
    action?: string;
    code?: string;
    message?: string;
    isFinal?: boolean;
    rawData?: Record<string, unknown>;
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export class VoiceBenchSession {
  private ws: WebSocket | null = null;
  private playerNode: AudioWorkletNode | null = null;
  private playerCtx: AudioContext | null = null;
  private recorderCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;

  // Committed turns (one per user isFinal, mobile-style) + a live partial
  // bubble that overwrites in place until the utterance is committed.
  private turns: Array<Turn & { guideDone: boolean }> = [];
  private liveUser = "";
  private nextTurnSeq = 0;

  constructor(
    private readonly guideId: string,
    private readonly cb: VoiceBenchCallbacks,
  ) {}

  async start(): Promise<void> {
    const [playerNode, playerCtx] = await startAudioPlayerWorklet();
    this.playerNode = playerNode;
    this.playerCtx = playerCtx;
    if (playerCtx.state === "suspended") await playerCtx.resume();

    // Relay the worklet's cumulative played-samples position to the server so
    // barge-in truncation reflects what the user actually HEARD (audio that
    // was buffered but flushed on interrupt was never heard). The payload uses
    // the schema's forward-compat "unknown" envelope.
    playerNode.port.onmessage = (event: MessageEvent) => {
      const played = event.data?.playedSamples;
      const skipped = event.data?.skippedSamples;
      if (typeof played === "number" && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify(this.envelope({
            eventType: "unknown",
            rawData: {
              action: "playback_progress",
              playedSamples: played,
              // Cumulative samples dropped by barge-in flushes — lets the
              // server map its bytes-SENT baselines into rendered space.
              skippedSamples: typeof skipped === "number" ? skipped : 0,
            },
          })),
        );
      }
    };

    const [, recorderCtx, micStream] = await startAudioRecorderWorklet((pcm: ArrayBuffer) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify(this.envelope({
            eventType: "message",
            data: arrayBufferToBase64(pcm),
            mimeType: "audio/pcm",
            role: "user",
          })),
        );
      }
    });
    this.recorderCtx = recorderCtx;
    this.micStream = micStream;

    const token = await getIdToken();
    const query = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `${WS_BASE_URL}/ws/studio/guide-voice-test/${encodeURIComponent(this.guideId)}${query}`;
    this.cb.onStatus("connecting…", false);

    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.cb.onStatus(`live — talking to ${this.guideId} (saved version)`, true);
      this.cb.onSystem(`voice session started with "${this.guideId}"`);
    };
    ws.onmessage = (event) => this.onMessage(event);
    ws.onclose = (event) => {
      this.cb.onSystem(`voice session closed${event.reason ? `: ${event.reason}` : ""}`);
      this.stop(false);
    };
    ws.onerror = () => this.cb.onStatus("connection error", false);
  }

  stop(closeWs = true): void {
    if (closeWs && this.ws && this.ws.readyState <= WebSocket.OPEN) this.ws.close();
    this.ws = null;
    if (this.micStream) {
      try { stopMicrophone(this.micStream); } catch { /* noop */ }
    }
    this.micStream = null;
    if (this.recorderCtx) { try { this.recorderCtx.close(); } catch { /* noop */ } this.recorderCtx = null; }
    if (this.playerCtx) { try { this.playerCtx.close(); } catch { /* noop */ } this.playerCtx = null; }
    this.playerNode = null;
    this.cb.onStatus("idle", false);
  }

  private envelope(payload: Record<string, unknown>) {
    return {
      eventId: crypto.randomUUID(),
      sessionId: this.guideId,
      timestamp: Date.now(),
      schemaVersion: "1.0",
      payload,
    };
  }

  private emitTurns(): void {
    const out: Turn[] = this.turns.map(({ id, user, guide, interruption }) => ({ id, user, guide, interruption }));
    if (this.liveUser) out.push({ id: "live", user: this.liveUser, guide: "" });
    this.cb.onTurns(out);
  }

  // Barge-in marker from the server: pin the guide bubble to what the user
  // actually HEARD and attach the intended/unspoken detail for the chip.
  private onInterruptionMarker(raw: Record<string, unknown>): void {
    const streaming = this.lastStreamingTurn() ?? this.turns[this.turns.length - 1];
    if (!streaming) return;
    const marker: InterruptionMarker = {
      interruptedAtMs: typeof raw.interruptedAtMs === "number" ? raw.interruptedAtMs : Date.now(),
      heardRatio: typeof raw.heardRatio === "number" ? raw.heardRatio : 0,
      intendedText: typeof raw.intendedText === "string" ? raw.intendedText : "",
      spokenText: typeof raw.spokenText === "string" ? raw.spokenText : "",
    };
    streaming.interruption = marker;
    streaming.guide = marker.spokenText || streaming.guide;
    streaming.guideDone = true;
    this.emitTurns();
  }

  private lastStreamingTurn(): (Turn & { guideDone: boolean }) | null {
    const last = this.turns[this.turns.length - 1];
    return last && !last.guideDone ? last : null;
  }

  private onUserText(text: string, isFinal: boolean): void {
    if (!isFinal) {
      // Deepgram user partials are full hypotheses — overwrite the live bubble.
      this.liveUser = text;
      return;
    }
    this.liveUser = "";
    if (!text) return; // empty final = server discarded the utterance
    // Mobile parity: a re-committed final before any guide text replaces the
    // previous pair instead of stacking a duplicate.
    const last = this.turns[this.turns.length - 1];
    if (last && !last.guideDone && !last.guide) this.turns.pop();
    this.turns.push({ id: `t-${this.nextTurnSeq++}`, user: text, guide: "", guideDone: false });
  }

  private onGuideText(text: string, isFinal: boolean): void {
    if (isFinal) {
      // Empty isFinal model message = end of the guide's reply.
      const streaming = this.lastStreamingTurn();
      if (streaming) streaming.guideDone = true;
      return;
    }
    if (!text) return;
    let turn = this.lastStreamingTurn();
    if (!turn) {
      // Guide spoke without a committed user turn (greeting).
      turn = { id: `t-${this.nextTurnSeq++}`, user: "", guide: "", guideDone: false };
      this.turns.push(turn);
    }
    turn.guide += text;
  }

  private onMessage(event: MessageEvent): void {
    const raw: WsEnvelope = JSON.parse(event.data);
    const payload = raw.payload;
    if (!payload) return;

    if (payload.eventType === "error") {
      this.cb.onSystem(`voice error ${payload.code}: ${payload.message}`);
      return;
    }
    if (payload.eventType === "control") {
      // interrupt / turn_completed both close the current guide bubble so the
      // next reply starts fresh.
      if (payload.action === "turn_completed" || payload.action === "interrupt") {
        const streaming = this.lastStreamingTurn();
        if (streaming) {
          streaming.guideDone = true;
          this.emitTurns();
        }
      }
      // Barge-in: the server stops SENDING on interrupt, but audio already in
      // the worklet's ring buffer would keep playing. endOfAudio makes the
      // worklet trail off (~300ms fade, like a human being interrupted) and
      // drop the rest.
      if (payload.action === "interrupt" && this.playerNode) {
        this.playerNode.port.postMessage({ command: "endOfAudio" });
      }
      return;
    }
    if (payload.eventType === "unknown") {
      const rawData = payload.rawData ?? {};
      if (rawData.action === "interruption_marker") {
        this.onInterruptionMarker(rawData);
      }
      return;
    }
    if (payload.eventType === "message") {
      if (payload.mimeType === "audio/pcm" && this.playerNode && payload.data) {
        this.playerNode.port.postMessage(base64ToArrayBuffer(payload.data));
      } else if (payload.mimeType === "text/plain") {
        if (payload.role === "user") {
          this.onUserText((payload.data || "").trim(), payload.isFinal === true);
        } else {
          this.onGuideText(payload.data || "", payload.isFinal === true);
        }
        this.emitTurns();
      }
    }
  }
}
